import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { Flex, getContrastingForeground, Position } from '@cash-design-system/react'
// Disabling the no-restricted-imports warning because the colors used are independent of light/dark mode.
// eslint-disable-next-line no-restricted-imports
import * as tokens from '@cash-design-system/tokens'
import { FormBlockerElementCaptionedTileElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'

import { SquiggleSvg } from './SquiggleSvg'

const Tile = styled.div<{ backgroundColor?: string }>`
  position: relative;
  width: 7.25rem;
  height: 11.25rem;
  border-radius: 1.5rem;
  ${props => (props.backgroundColor ? `background-color: ${props.backgroundColor}` : '')};
  box-shadow: ${props => props.theme.dropShadows.tile};
  padding: ${({ theme }) => theme.spacing.m};
`

const Icon = styled.img`
  position: absolute;
  top: 1rem;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
`

const Caption = styled.div`
  height: 2rem;
  position: absolute;
  bottom: 0.825rem;
  width: 100%;
  padding: 0 0.875rem;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
`

const CaptionText = styled.span<{ textColor: string; fontSize: string }>`
  font-size: ${props => props.fontSize}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  font-weight: ${props => props.theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  color: ${props => props.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const fontSizeByTextLength = (text: string) => {
  if (text.length > 6) return '1rem'
  if (text.length > 4) return '1.25rem'
  return '1.5rem'
}

export const CaptionedTileElement = ({
  icon,
  background_color,
  caption,
  accessibility_text,
}: FormBlockerElementCaptionedTileElement) => {
  const theme = useTheme()

  const backgroundColor = theme.darkMode ? background_color?.dark?.srgb : background_color?.light?.srgb
  const iconUrl = theme.darkMode ? icon?.dark_url : icon?.light_url

  const darkForegroundColor = tokens.ColorPaletteCobalt
  const lightForegroundColor = tokens.ColorPaletteWhite

  const captionTextColor = backgroundColor
    ? getContrastingForeground({
        backgroundColor,
        darkForegroundColor,
        lightForegroundColor,
      })
    : theme.colors.label

  return (
    <Flex justifyContent="center">
      <Tile backgroundColor={backgroundColor} role="img" aria-label={accessibility_text}>
        {iconUrl && (
          <Flex justifyContent="center">
            <Icon src={iconUrl} data-testid="tile-icon" />
          </Flex>
        )}
        <Position position="absolute" top="4.2rem" left="0">
          <SquiggleSvg color={captionTextColor} />
        </Position>
        {caption && (
          <Flex justifyContent="center">
            <Caption>
              <CaptionText textColor={captionTextColor} fontSize={fontSizeByTextLength(caption)}>
                {caption}
              </CaptionText>
            </Caption>
          </Flex>
        )}
      </Tile>
    </Flex>
  )
}
